import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, QueryStateBoundary } from "@/components/ui";
import { useEventsCalendar } from "@/api/hooks/useEvents";
import { EVENT_STATUS_BADGE_VARIANT, EVENT_STATUS_LABELS, type Event } from "@/api/types/event";
import { currentMonth, formatMonthLabel, getMonthGridDays, shiftMonth } from "./eventDate";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function EventCalendarPage() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(currentMonth());
  const { data, isLoading, error, refetch } = useEventsCalendar(month);

  const days = useMemo(() => getMonthGridDays(month), [month]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, Event[]>();
    for (const event of data?.events ?? []) {
      const list = map.get(event.event_date) ?? [];
      list.push(event);
      map.set(event.event_date, list);
    }
    return map;
  }, [data]);

  const tasksDueByDay = useMemo(() => {
    const map = new Map<string, number>();
    for (const event of data?.events ?? []) {
      for (const task of event.tasks ?? []) {
        if (!task.due_date || task.status !== "open") continue;
        map.set(task.due_date, (map.get(task.due_date) ?? 0) + 1);
      }
    }
    return map;
  }, [data]);

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-display text-2xl font-bold text-text-primary">Events calendar</h1>
        <Button variant="secondary" onClick={() => navigate("/events")}>
          List view
        </Button>
      </div>

      <div className="mb-4 flex items-center justify-center gap-4">
        <Button size="sm" variant="secondary" onClick={() => setMonth((m) => shiftMonth(m, -1))} aria-label="Previous month">
          ← Prev
        </Button>
        <h2 className="min-w-[10rem] text-center text-lg font-semibold text-text-primary" data-testid="calendar-month-label">
          {formatMonthLabel(month)}
        </h2>
        <Button size="sm" variant="secondary" onClick={() => setMonth((m) => shiftMonth(m, 1))} aria-label="Next month">
          Next →
        </Button>
      </div>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        <div className="overflow-x-auto">
          <div className="grid min-w-[640px] grid-cols-7 gap-px rounded-lg border border-border bg-border">
            {WEEKDAY_LABELS.map((label) => (
              <div key={label} className="bg-bg-hover px-2 py-1 text-center text-xs font-semibold uppercase text-text-muted">
                {label}
              </div>
            ))}
            {days.map((day) => {
              const dayEvents = eventsByDay.get(day.iso) ?? [];
              const tasksDue = tasksDueByDay.get(day.iso) ?? 0;
              return (
                <div
                  key={day.iso}
                  data-day={day.iso}
                  className={`min-h-[6rem] bg-bg-card p-1.5 ${day.inMonth ? "" : "opacity-40"} ${
                    day.isToday ? "ring-2 ring-inset ring-primary" : ""
                  }`}
                >
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs font-medium text-text-secondary">{day.date.getDate()}</span>
                    {tasksDue > 0 && (
                      <span
                        className="h-1.5 w-1.5 rounded-full bg-warning"
                        title={`${tasksDue} task(s) due`}
                        data-testid={`tasks-due-dot-${day.iso}`}
                      />
                    )}
                  </div>
                  <div className="space-y-1">
                    {dayEvents.map((event) => (
                      <button
                        key={event.id}
                        type="button"
                        onClick={() => navigate(`/events/${event.id}`)}
                        className="block w-full truncate rounded px-1 py-0.5 text-left text-xs hover:bg-bg-hover"
                        data-event-chip={event.id}
                      >
                        <Badge variant={EVENT_STATUS_BADGE_VARIANT[event.status]} size="sm" className="mr-1">
                          {EVENT_STATUS_LABELS[event.status]}
                        </Badge>
                        {event.venue_name ?? "Event"}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </QueryStateBoundary>
    </div>
  );
}
